import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Phone, Mail, FileText, Eye } from 'lucide-react';

const ClientsList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    nom: '', prenom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '', type: 'particulier'
  });
  const [selectedClientDevis, setSelectedClientDevis] = useState(null);
  const [devisDialogOpen, setDevisDialogOpen] = useState(false);

  useEffect(() => {
    loadClients();
  }, [type]);

  const loadClients = async () => {
    try {
      const params = new URLSearchParams();
      if (type !== 'all') {
        params.append('type', type);
      }
      const res = await axios.get(`${API}/clients`, { params, withCredentials: true });
      setClients(res.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData, { withCredentials: true });
        toast.success('Client mis à jour');
      } else {
        // Vérifier les doublons avant de créer
        const checkRes = await axios.post(`${API}/clients/check-duplicate`, {
          email: formData.email,
          telephone: formData.telephone
        }, { withCredentials: true });
        
        if (checkRes.data.exists) {
          const existingClient = checkRes.data.client;
          toast.warning(`⚠️ Doublon détecté : ${existingClient?.prenom || ''} ${existingClient?.nom || ''} (${existingClient?.email || ''}, ${existingClient?.telephone || ''})`);
          return;
        }
        
        await axios.post(`${API}/clients`, formData, { withCredentials: true });
        toast.success('Client créé');
      }
      loadClients();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(errorMsg);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      nom: client.nom,
      prenom: client.prenom,
      email: client.email,
      telephone: client.telephone,
      adresse: client.adresse || '',
      code_postal: client.code_postal || '',
      ville: client.ville || '',
      type: client.type || 'particulier'
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    try {
      await axios.delete(`${API}/clients/${id}`, { withCredentials: true });
      toast.success('Client supprimé');
      loadClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({ nom: '', prenom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '', type: 'particulier' });
  };

  const handleViewDevis = async (client) => {
    try {
      const res = await axios.get(`${API}/clients/${client.id}/devis`, { withCredentials: true });
      setSelectedClientDevis({ client, devis: res.data });
      setDevisDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des devis');
    }
  };

  const filteredClients = clients.filter(c =>
    `${c.nom} ${c.prenom} ${c.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Clients</h1>
          <p className="text-stone-600">Gérez vos clients</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="btn-add-client">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    data-testid="input-client-nom"
                  />
                </div>
                <div>
                  <Label>Prénom *</Label>
                  <Input
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    required
                    data-testid="input-client-prenom"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="input-client-email"
                  />
                </div>
                <div>
                  <Label>Téléphone *</Label>
                  <Input
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    required
                    data-testid="input-client-telephone"
                  />
                </div>
              </div>
              <div>
                <Label>Adresse</Label>
                <Input
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Code postal</Label>
                  <Input
                    value={formData.code_postal}
                    onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Input
                    value={formData.ville}
                    onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Type de client *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                  required
                >
                  <SelectTrigger data-testid="select-client-type">
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="particulier">Particulier</SelectItem>
                    <SelectItem value="partenaire">Partenaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="btn-save-client">
                  {editingClient ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              placeholder="Rechercher un client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-clients"
            />
          </div>
          <div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrer par type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="particulier">Particulier</SelectItem>
                <SelectItem value="partenaire">Partenaire</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead className="text-center">Devis</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                    Aucun client trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                    <TableCell>
                      <div className="font-medium">{client.prenom} {client.nom}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={client.type === 'partenaire' ? 'default' : 'outline'}>
                        {client.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-1 text-sm">
                          <Mail className="h-3 w-3" /> {client.email}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-stone-500">
                          <Phone className="h-3 w-3" /> {client.telephone}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{client.ville || '-'}</TableCell>
                    <TableCell className="text-center">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleViewDevis(client)}
                        className="flex items-center gap-1"
                      >
                        <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                          {client.devis_count || 0}
                        </Badge>
                        <Eye className="h-4 w-4 text-stone-500" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/backoffice/clients/${client.id}`} data-testid={`view-client-${client.id}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(client)} data-testid={`edit-client-${client.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(client.id)} data-testid={`delete-client-${client.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal pour voir les devis d'un client */}
      <Dialog open={devisDialogOpen} onOpenChange={setDevisDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Devis de {selectedClientDevis?.client?.prenom} {selectedClientDevis?.client?.nom}
            </DialogTitle>
          </DialogHeader>
          
          {selectedClientDevis?.devis?.length > 0 ? (
            <div className="space-y-3">
              {selectedClientDevis.devis.map((devis) => (
                <div key={devis.id} className="border rounded-lg p-4 hover:bg-stone-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-stone-800">{devis.numero}</h3>
                      <p className="text-sm text-stone-600 mt-1">
                        {devis.event_info?.event_type?.replace('_', ' ')} - {devis.event_info?.vedettes}
                      </p>
                      <p className="text-xs text-stone-500 mt-1">
                        Créé le {new Date(devis.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-stone-800">{devis.total_ttc?.toLocaleString()} €</p>
                      <Badge className={
                        devis.statut === 'valide' || devis.statut === 'signe' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : devis.statut === 'envoye' 
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-stone-100 text-stone-600'
                      }>
                        {devis.statut === 'brouillon' && '🟡 Brouillon'}
                        {devis.statut === 'envoye' && '📤 Envoyé'}
                        {devis.statut === 'valide' && '✅ Validé'}
                        {devis.statut === 'signe' && '✍️ Signé'}
                        {devis.statut === 'annule' && '❌ Annulé'}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Link to={`/devis/${devis.id}`} target="_blank">
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        Voir le devis
                      </Button>
                    </Link>
                    <Link to={`/backoffice/devis/${devis.id}/edit`}>
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4 mr-1" />
                        Modifier
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-stone-500">Aucun devis pour ce client</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsList;
